import { useState, useRef, useEffect, useCallback } from "react";
import '../helpdesk-analytics.css';

const FUNCTION_APP_URL = "https://ima-response-functions-fud2d0cwhechfyf3.centralus-01.azurewebsites.net";
const FUNCTION_KEY     = "OvFAxromVEnpQIFXrZi_7bujcDKMKGWLqNDLpvQsCuoqAzFu_Nwb9w==";

// ── Exact copies of original helper functions ─────────────────────────────────
const COLORS = ['#4a9eff','#3ecf8e','#f5a623','#a78bfa','#2dd4bf','#f06060','#6ee7b7','#fbbf24','#c084fc','#67e8f9'];

let _chartId = 0;

function fmtDate(d) {
  const p = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`;
}

function isClosed(t) {
  if (t._is_closed !== undefined) return t._is_closed;
  return t.status_id === 2;
}

function pick(obj, ...fields) {
  for (const f of fields) {
    const v = obj[f];
    if (v !== undefined && v !== null && v !== '' && v !== 0) return String(v);
  }
  return 'Unknown';
}

function byField(tickets, ...fields) {
  const map = {};
  tickets.forEach(t => { const v = pick(t, ...fields); map[v] = (map[v]||0)+1; });
  return Object.entries(map).sort((a,b) => b[1]-a[1]).slice(0,12);
}

function getCategory(t, topLevelOnly) {
  const cat = t.category_1_display || t.category_1 || t.category_2_display || '';
  if (!cat || cat === 'Uncategorised') return null;
  return topLevelOnly ? cat.split('>')[0].trim() : cat;
}

function byCategoryField(tickets, topLevelOnly) {
  const map = {};
  tickets.forEach(t => { const v = getCategory(t, topLevelOnly); if (v) map[v] = (map[v]||0)+1; });
  return Object.entries(map).sort((a,b) => b[1]-a[1]).slice(0,12);
}

function avgHours(tickets) {
  const r = tickets.filter(t => isClosed(t) && t.dateoccurred && t.lastactiondate);
  if (!r.length) return null;
  const hrs = r.map(t => (new Date(t.lastactiondate)-new Date(t.dateoccurred))/3600000).filter(h=>h>=0&&h<8760);
  return hrs.length ? (hrs.reduce((a,b)=>a+b,0)/hrs.length).toFixed(1) : null;
}

function barBlock(title, entries, colorIdx=0) {
  const id = 'chart-'+(++_chartId);
  const color = COLORS[colorIdx % COLORS.length];
  if (!entries.length) return `<div class="chart-card" id="${id}"><div class="chart-title">${title}</div><div style="color:var(--text3);font-size:12px">No data</div></div>`;
  const max = entries[0][1]||1;
  const rows = entries.map(([k,v]) =>
    `<div class="bar-row"><div class="bar-label" title="${k}">${k}</div>`+
    `<div class="bar-track"><div class="bar-fill" style="width:${Math.round(v/max*100)}%;background:${color}"></div></div>`+
    `<div class="bar-count">${v}</div></div>`
  ).join('');
  return `<div class="chart-card" id="${id}"><div class="chart-title">${title}</div>${rows}</div>`;
}

function categoryBarBlock(tickets, colorIdx=1, catTopLevel=false) {
  const id = 'chart-cat-'+(++_chartId);
  const entries = byCategoryField(tickets, catTopLevel);
  const color = COLORS[colorIdx % COLORS.length];
  const max = entries.length ? entries[0][1] : 1;
  const rows = entries.map(([k,v]) =>
    `<div class="bar-row"><div class="bar-label" title="${k}">${k}</div>`+
    `<div class="bar-track"><div class="bar-fill" style="width:${Math.round(v/max*100)}%;background:${color}"></div></div>`+
    `<div class="bar-count">${v}</div></div>`
  ).join('') || `<div style="color:var(--text3);font-size:12px">No data</div>`;
  return `<div class="chart-card" id="${id}"><div class="chart-title">Tickets by category</div>${rows}</div>`;
}

function sectionHdr(title, range, count) {
  const countStr = count !== undefined ? ` <span style="color:var(--blue);font-family:var(--mono)">${count}</span>` : '';
  return `<div class="section-hdr"><h2>${title}</h2><span>${range}${countStr}</span></div>`;
}

// ── View functions — exact copies of original ─────────────────────────────────
function viewOverview(d, catTopLevel) {
  const t = d.tickets;
  const total     = t.length;
  const openCnt   = t.filter(x => !isClosed(x)).length;
  const closedCnt = t.filter(x =>  isClosed(x)).length;
  const avg       = avgHours(t);
  // Use same calculation as SLA view — fixbydate in past on open tickets
  // (HaloITSM list API doesn't populate sla_breached flag on paginated results)
  const breached  = t.filter(x => x.excludefromsla === false && x.fixbydate && new Date(x.fixbydate) < new Date() && !isClosed(x)).length;
  return sectionHdr('Overview', `${d.from} — ${d.to}`, total) +
  `<div class="metric-grid">
    <div class="metric-card"><div class="metric-label">Total tickets</div><div class="metric-value">${total}</div></div>
    <div class="metric-card"><div class="metric-label">Open</div><div class="metric-value metric-amber">${openCnt}</div></div>
    <div class="metric-card"><div class="metric-label">Closed</div><div class="metric-value metric-green">${closedCnt}</div></div>
    <div class="metric-card"><div class="metric-label">Avg resolution</div><div class="metric-value metric-blue">${avg||'—'}</div><div class="metric-sub">hours</div></div>
    <div class="metric-card"><div class="metric-label">SLA breaches</div><div class="metric-value ${breached?'metric-red':'metric-green'}">${breached}</div></div>
  </div>
  <div class="charts-row">
    ${barBlock('Tickets by agent', byField(t,'_agent_name'), 0)}
    ${categoryBarBlock(t, 1, catTopLevel)}
  </div>
  <div class="charts-row">
    ${barBlock('Tickets by type', byField(t,'_type_name'), 2)}
    ${barBlock('Tickets by priority', byField(t,'_priority_name'), 3)}
  </div>`;
}

function viewStaff(d) {
  const map = {};
  d.tickets.forEach(t => {
    const name = t._agent_name || 'Unassigned';
    if (!map[name]) map[name] = { name, total:0, open:0, closed:0, hrs:[] };
    map[name].total++;
    if (isClosed(t)) {
      map[name].closed++;
      if (t.dateoccurred && t.lastactiondate) {
        const h = (new Date(t.lastactiondate)-new Date(t.dateoccurred))/3600000;
        if (h>=0) map[name].hrs.push(h);
      }
    } else map[name].open++;
  });
  const rows = Object.values(map).sort((a,b)=>b.total-a.total).map(a => {
    const avg  = a.hrs.length ? (a.hrs.reduce((x,y)=>x+y,0)/a.hrs.length).toFixed(1)+'h' : '—';
    const rate = a.total ? Math.round(a.closed/a.total*100) : 0;
    const cls  = rate>=80 ? 'badge-green' : rate>=50 ? 'badge-amber' : 'badge-red';
    return `<tr>
      <td>${a.name}</td><td class="mono">${a.total}</td>
      <td class="mono" style="color:var(--amber)">${a.open}</td>
      <td class="mono" style="color:var(--green)">${a.closed}</td>
      <td class="mono">${avg}</td>
      <td><span class="badge ${cls}">${rate}%</span></td>
    </tr>`;
  }).join('');
  return sectionHdr('Staff performance', `${d.from} — ${d.to}`, d.tickets.length) +
  `<div class="table-wrap"><div class="tbl-title">Agent breakdown</div>
    <table><thead><tr><th>Agent</th><th>Total</th><th>Open</th><th>Closed</th><th>Avg resolve</th><th>Close rate</th></tr></thead>
    <tbody>${rows||'<tr><td colspan="6" style="color:var(--text3);padding:20px">No agent data found</td></tr>'}</tbody></table>
  </div>`;
}

function viewSources(d, catTopLevel) {
  const t = d.tickets;
  return sectionHdr('Ticket sources', `${d.from} — ${d.to}`, d.tickets.length) +
  `<div class="charts-row">
    ${categoryBarBlock(t, 0, catTopLevel)}
    ${barBlock('By team', byField(t,'team'), 1)}
  </div>
  <div class="charts-row">
    ${barBlock('By priority', byField(t,'_priority_name'), 2)}
    ${barBlock('By division (AzureAD company)', byField(t,'_division'), 3)}
  </div>`;
}

function viewSLA(d) {
  const t = d.tickets;
  const breached = t.filter(x => x.excludefromsla===false && x.fixbydate && new Date(x.fixbydate)<new Date() && !isClosed(x)).length;
  const resolved = t.filter(x => isClosed(x));
  const avg = avgHours(t);
  const slaRate = t.length ? Math.round(resolved.length/t.length*100) : null;
  const buckets = {'< 1 hour':0,'1 – 4 hours':0,'4 – 8 hours':0,'8 – 24 hours':0,'1 – 3 days':0,'> 3 days':0};
  t.filter(x=>isClosed(x)&&x.dateoccurred&&x.lastactiondate).forEach(tk=>{
    const h=(new Date(tk.lastactiondate)-new Date(tk.dateoccurred))/3600000;
    if(h<1) buckets['< 1 hour']++;
    else if(h<4) buckets['1 – 4 hours']++;
    else if(h<8) buckets['4 – 8 hours']++;
    else if(h<24) buckets['8 – 24 hours']++;
    else if(h<72) buckets['1 – 3 days']++;
    else buckets['> 3 days']++;
  });
  const agentAvg={};
  t.filter(x=>isClosed(x)&&x.dateoccurred&&x.lastactiondate).forEach(tk=>{
    const n=tk._agent_name||'Unassigned';
    if(!agentAvg[n]) agentAvg[n]=[];
    const h=(new Date(tk.lastactiondate)-new Date(tk.dateoccurred))/3600000;
    if(h>=0) agentAvg[n].push(h);
  });
  const agentAvgEntries=Object.entries(agentAvg).map(([k,v])=>[k,+(v.reduce((a,b)=>a+b,0)/v.length).toFixed(1)]).sort((a,b)=>a[1]-b[1]).slice(0,10);
  const bkEntries=Object.entries(buckets);
  const bkMax=Math.max(...bkEntries.map(x=>x[1]),1);
  const closedHrs=t.filter(x=>isClosed(x)&&x.dateoccurred&&x.lastactiondate).map(x=>(new Date(x.lastactiondate)-new Date(x.dateoccurred))/3600000).filter(h=>h>=0);
  const pct=hrs=>closedHrs.length?Math.round(closedHrs.filter(h=>h<=hrs).length/closedHrs.length*100):0;
  const p4=pct(4),p6=pct(6),p8=pct(8),p24=pct(24);
  return sectionHdr('SLA & resolution time',`${d.from} — ${d.to}`,d.tickets.length)+
  `<div class="metric-grid">
    <div class="metric-card"><div class="metric-label">Avg resolution</div><div class="metric-value metric-blue">${avg||'—'}</div><div class="metric-sub">hours</div></div>
    <div class="metric-card"><div class="metric-label">SLA compliance</div><div class="metric-value ${slaRate>=80?'metric-green':slaRate>=60?'metric-amber':'metric-red'}">${slaRate!=null?slaRate+'%':'—'}</div></div>
    <div class="metric-card"><div class="metric-label">SLA breached</div><div class="metric-value ${breached?'metric-red':'metric-green'}">${breached}</div></div>
    <div class="metric-card"><div class="metric-label">Resolved tickets</div><div class="metric-value">${resolved.length}</div></div>
  </div>
  <div class="section-hdr" style="margin-top:4px"><h2>Closed within time threshold</h2></div>
  <div class="metric-grid" style="margin-bottom:22px">
    <div class="metric-card"><div class="metric-label">Within 4 hours</div><div class="metric-value ${p4>=80?'metric-green':p4>=50?'metric-amber':'metric-red'}">${p4}%</div><div class="metric-sub">${closedHrs.filter(h=>h<=4).length} tickets</div></div>
    <div class="metric-card"><div class="metric-label">Within 6 hours</div><div class="metric-value ${p6>=80?'metric-green':p6>=50?'metric-amber':'metric-red'}">${p6}%</div><div class="metric-sub">${closedHrs.filter(h=>h<=6).length} tickets</div></div>
    <div class="metric-card"><div class="metric-label">Within 8 hours</div><div class="metric-value ${p8>=80?'metric-green':p8>=50?'metric-amber':'metric-red'}">${p8}%</div><div class="metric-sub">${closedHrs.filter(h=>h<=8).length} tickets</div></div>
    <div class="metric-card"><div class="metric-label">Within 24 hours</div><div class="metric-value ${p24>=80?'metric-green':p24>=50?'metric-amber':'metric-red'}">${p24}%</div><div class="metric-sub">${closedHrs.filter(h=>h<=24).length} tickets</div></div>
  </div>
  <div class="charts-row">
    <div class="chart-card"><div class="chart-title">Resolution time distribution</div>
      ${bkEntries.map(([k,v])=>`<div class="bar-row"><div class="bar-label" title="${k}">${k}</div><div class="bar-track"><div class="bar-fill" style="width:${Math.round(v/bkMax*100)}%;background:#4a9eff"></div></div><div class="bar-count">${v}</div></div>`).join('')}
    </div>
    ${barBlock('Avg resolution by agent (hours)',agentAvgEntries,2)}
  </div>`;
}

function viewTrends(d) {
  const weekly={};
  d.tickets.forEach(t=>{
    if(!t.dateoccurred) return;
    const dt=new Date(t.dateoccurred); if(isNaN(dt)) return;
    const day=dt.getDay();
    const mon=new Date(dt); mon.setDate(dt.getDate()-(day===0?6:day-1));
    const wk=fmtDate(mon);
    if(!weekly[wk]) weekly[wk]={open:0,closed:0};
    isClosed(t)?weekly[wk].closed++:weekly[wk].open++;
  });
  const weeks=Object.keys(weekly).sort();
  const maxVal=Math.max(...weeks.map(w=>weekly[w].open+weekly[w].closed),1);
  const rows=weeks.map(w=>{
    const {open,closed}=weekly[w];
    return `<div class="trend-row">
      <div class="trend-week">${w}</div>
      <div class="trend-bars">
        <div class="trend-bar-wrap"><div class="trend-bar-track"><div class="trend-bar-fill" style="width:${Math.round(closed/maxVal*100)}%;background:#4a9eff"></div></div><div class="trend-count">${closed}</div></div>
        <div class="trend-bar-wrap"><div class="trend-bar-track"><div class="trend-bar-fill" style="width:${Math.round(open/maxVal*100)}%;background:#f5a623"></div></div><div class="trend-count">${open}</div></div>
      </div></div>`;
  }).join('')||`<div style="color:var(--text3);font-size:12px">No dated tickets found</div>`;
  const totalOpen=d.tickets.filter(x=>!isClosed(x)).length;
  const totalClosed=d.tickets.filter(x=>isClosed(x)).length;
  return sectionHdr('Open / closed trends',`${d.from} — ${d.to}`,d.tickets.length)+
  `<div class="metric-grid">
    <div class="metric-card"><div class="metric-label">Total open</div><div class="metric-value metric-amber">${totalOpen}</div></div>
    <div class="metric-card"><div class="metric-label">Total closed</div><div class="metric-value metric-green">${totalClosed}</div></div>
    <div class="metric-card"><div class="metric-label">Weeks in range</div><div class="metric-value">${weeks.length}</div></div>
  </div>
  <div class="chart-card" style="margin-bottom:20px">
    <div class="chart-title">Weekly volume
      <span style="font-size:11px;font-weight:400;margin-left:12px">
        <span style="color:#4a9eff">▬</span> closed &nbsp;
        <span style="color:#f5a623">▬</span> open
      </span>
    </div>
    ${rows}
  </div>`;
}

function viewTypes(d) {
  const t = d.tickets;
  return sectionHdr('Ticket types',`${d.from} — ${d.to}`,d.tickets.length)+
  `<div class="charts-row">
    ${barBlock('By ticket type',byField(t,'_type_name'),3)}
    ${barBlock('By status',byField(t,'_status_name'),1)}
  </div>
  <div class="charts-row">
    ${barBlock('By team',byField(t,'team'),0)}
    ${barBlock('By category',byField(t,'_category'),2)}
  </div>`;
}

const VIEW_FNS = { Overview: viewOverview, Staff: viewStaff, Sources: viewSources, SLA: viewSLA, Trends: viewTrends, Types: viewTypes };
const VIEWS = ['Overview','Staff','Sources','SLA','Trends','Types'];

// ── Filter pill — exact original behavior ─────────────────────────────────────
function FilterPill({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const count = selected.size;
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="ms-wrap" ref={ref} style={{ zIndex: open ? 500 : 'auto' }}>
      <div className={`ms-pill${count ? ' active' : ''}`} onClick={() => setOpen(v => !v)}>
        <span className="ms-label">{count===1 ? [...selected][0].slice(0,18) : label}</span>
        {count > 1 && <span className="ms-badge">{count}</span>}
        <span className="ms-caret">▾</span>
      </div>
      {open && (
        <div className="ms-dropdown open">
          <input className="ms-search" placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)} autoFocus />
          <div className="ms-options">
            {!filtered.length && <div style={{padding:'10px 12px',fontSize:12,color:'var(--text3)'}}>No options</div>}
            {filtered.map(o => (
              <label key={o} className={`ms-option${selected.has(o)?' selected':''}`}>
                <input type="checkbox" checked={selected.has(o)} onChange={e => {
                  const s = new Set(selected); e.target.checked ? s.add(o) : s.delete(o); onChange(s);
                }} />
                <span>{o}</span>
              </label>
            ))}
          </div>
          <div className="ms-footer">
            <span className="ms-footer-btn" onClick={() => onChange(new Set(filtered))}>Select all</span>
            <span className="ms-footer-btn" onClick={() => { onChange(new Set()); setSearch(''); }}>Clear</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function ReportsPage() {
  const now = new Date();
  const [dateFrom, setDateFrom] = useState(fmtDate(new Date(now - 30*86400000)));
  const [dateTo,   setDateTo]   = useState(fmtDate(now));
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [rawData,  setRawData]  = useState(null); // { tickets: [...], from, to }
  const [view,     setView]     = useState('Overview');
  const [hasRun,   setHasRun]   = useState(false);
  const [catTopLevel, setCatTopLevel] = useState(false);
  const runRef = useRef(0);

  // Filters — sets of selected values
  const [fAgent,    setFAgent]    = useState(new Set());
  const [fTeam,     setFTeam]     = useState(new Set());
  const [fType,     setFType]     = useState(new Set());
  const [fPriority, setFPriority] = useState(new Set());
  const [fStatus,   setFStatus]   = useState(new Set());
  const [fDivision, setFDivision] = useState(new Set());

  const clearFilters = () => { setFAgent(new Set()); setFTeam(new Set()); setFType(new Set()); setFPriority(new Set()); setFStatus(new Set()); setFDivision(new Set()); };
  const hasFilters = fAgent.size||fTeam.size||fType.size||fPriority.size||fStatus.size||fDivision.size;

  // Get filtered tickets — exact copy of original getFilteredTickets()
  const getFilteredTickets = useCallback((tickets) => {
    if (!tickets) return [];
    return tickets.filter(t =>
      (!fAgent.size    || fAgent.has(t._agent_name))    &&
      (!fTeam.size     || fTeam.has(t.team))             &&
      (!fType.size     || fType.has(t._type_name))       &&
      (!fPriority.size || fPriority.has(t._priority_name)) &&
      (!fStatus.size   || fStatus.has(t._status_name))  &&
      (!fDivision.size || fDivision.has(t._division))
    );
  }, [fAgent, fTeam, fType, fPriority, fStatus, fDivision]);

  // Build filter options from all tickets (not filtered)
  const allTickets = rawData?.tickets || [];
  const optAgents     = [...new Set(allTickets.map(t=>t._agent_name).filter(Boolean))].sort();
  const optTeams      = [...new Set(allTickets.map(t=>t.team).filter(Boolean))].sort();
  const optTypes      = [...new Set(allTickets.map(t=>t._type_name).filter(Boolean))].sort();
  const optPriorities = [...new Set(allTickets.map(t=>t._priority_name).filter(Boolean))].sort();
  const optStatuses   = [...new Set(allTickets.map(t=>t._status_name).filter(Boolean))].sort();
  const optDivisions  = [...new Set(allTickets.map(t=>t._division).filter(Boolean))].sort();

  // Build view data with filtered tickets
  const filteredTickets = getFilteredTickets(allTickets);
  const viewData = rawData ? { ...rawData, tickets: filteredTickets } : null;

  // Generate HTML
  const html = viewData ? (() => {
    _chartId = 0;
    const fn = VIEW_FNS[view];
    return fn ? fn(viewData, catTopLevel) : '';
  })() : '';

  async function runReport() {
    const runId = ++runRef.current;
    setLoading(true); setError(''); setHasRun(true); setRawData(null); clearFilters();
    try {
      const params = new URLSearchParams({ dateFrom, dateTo });
      const res = await fetch(`${FUNCTION_APP_URL}/api/halo-tickets?${params}`, {
        headers: { 'x-functions-key': FUNCTION_KEY },
      });
      if (runRef.current !== runId) return;
      const txt = await res.text();
      if (runRef.current !== runId) return;
      if (!res.ok) throw new Error(`API error ${res.status}: ${txt.slice(0,200)}`);
      if (!txt?.trim()) throw new Error('Empty response');
      let d;
      try { d = JSON.parse(txt); } catch(e) { throw new Error(`Parse error (${txt.length}B): ${txt.slice(0,100)}`); }
      setRawData(d);
    } catch(e) {
      if (runRef.current !== runId) return;
      setError(e.message);
    } finally {
      if (runRef.current === runId) setLoading(false);
    }
  }

  function exportCSV() {
    if (!viewData) return;
    const t = viewData.tickets;
    const rows = [
      ['ID','Date','Agent','Team','Type','Priority','Status','Category','Summary'],
      ...t.map(x=>[x.id, x.dateoccurred?.slice(0,10), x._agent_name, x.team, x._type_name, x._priority_name, x._status_name, x._category, `"${(x.summary||'').replace(/"/g,'""')}"` ])
    ];
    const csv = rows.map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
    a.download = `halo-report-${dateFrom}-${dateTo}.csv`;
    a.click();
  }

  const ticketCountText = viewData
    ? (hasFilters ? `Showing ${filteredTickets.length} of ${allTickets.length} tickets` : `${allTickets.length} tickets`)
    : '';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>

      {/* Topbar */}
      <div className="topbar">
        <div style={{ marginLeft:'auto' }} />
        <div className="date-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
          <label>To</label>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={runReport} disabled={loading}>
          {loading ? 'Loading…' : 'Run report'}
        </button>
        {viewData && <button className="btn" onClick={exportCSV}>Export CSV</button>}
        {viewData && <button className="btn" onClick={()=>window.print()}>⎙ PDF</button>}
      </div>

      {/* Filter bar */}
      {rawData && (
        <div className="filter-bar">
          <FilterPill label="Agent"       options={optAgents}     selected={fAgent}    onChange={setFAgent}    />
          <div className="filter-divider"/>
          <FilterPill label="Team"        options={optTeams}      selected={fTeam}     onChange={setFTeam}     />
          <div className="filter-divider"/>
          <FilterPill label="Ticket type" options={optTypes}      selected={fType}     onChange={setFType}     />
          <div className="filter-divider"/>
          <FilterPill label="Priority"    options={optPriorities} selected={fPriority} onChange={setFPriority} />
          <div className="filter-divider"/>
          <FilterPill label="Status"      options={optStatuses}   selected={fStatus}   onChange={setFStatus}   />
          <div className="filter-divider"/>
          <FilterPill label="Division"    options={optDivisions}  selected={fDivision} onChange={setFDivision} />
          {!!hasFilters && (
            <span className="filter-clear" onClick={clearFilters}>✕ Clear all</span>
          )}
          <span className="filter-count" id="filter-count" dangerouslySetInnerHTML={{ __html: ticketCountText }} />
        </div>
      )}

      {/* Sub-nav */}
      {rawData && (
        <div style={{ background:'var(--bg2)', borderBottom:'1px solid var(--border)', padding:'0 24px', display:'flex', flexShrink:0 }}>
          {VIEWS.map(v => (
            <button key={v} onClick={()=>setView(v)} style={{
              padding:'10px 16px', fontSize:12, fontFamily:'var(--font)', cursor:'pointer',
              background:'transparent', border:'none',
              borderBottom: view===v ? '2px solid var(--blue)' : '2px solid transparent',
              color: view===v ? 'var(--blue)' : 'var(--text2)',
              fontWeight: view===v ? 500 : 400,
            }}>{v}</button>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding: rawData?'24px':'0', scrollbarWidth:'thin', scrollbarColor:'var(--bg3) transparent' }}>
        {error && <div style={{ background:'var(--red-dim)', border:'1px solid rgba(240,96,96,.25)', borderRadius:8, padding:'12px 16px', fontSize:13, color:'var(--red)', margin:24 }}>⚠ {error}</div>}

        {!hasRun && !loading && (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <div className="empty-title">HaloITSM Ticket Analytics</div>
            <div className="empty-sub">Select a date range and click <strong>Run report</strong> to load helpdesk data.</div>
          </div>
        )}

        {loading && (
          <div className="empty-state">
            <div style={{ width:36, height:36, border:'2px solid var(--border2)', borderTopColor:'var(--blue)', borderRadius:'50%', animation:'spin .8s linear infinite' }} />
            <div className="empty-title">Loading tickets from HaloITSM…</div>
            <div style={{ fontSize:12, color:'var(--text3)', fontFamily:'var(--mono)' }}>This takes about 15 seconds</div>
          </div>
        )}

        {viewData && !loading && (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
    </div>
  );
}

export { ReportsPage };
